import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useSetSelectionAction } from '@app/modules/logger/screens/stream/stores/selection.store';
import {
  useSetCursor,
  useSetFocus,
  useSetFocusCenteredAtTs,
  useSetUserRange,
  useStreamUserDefinedRange,
} from '@app/modules/logger/screens/stream/stores/stream-screen.store';
import debugFn from 'debug';
import { useEffect } from 'react';
import { STREAM_SCREEN_CONFIG } from '../../config';
import { calculateFocusFromRange } from '../../helpers/calculate-focus-from-range';
import { calculateRangeFromConfig } from '../../helpers/calculate-range-from-config';
import { LoggerParamsWithRange, useLoggerParams } from './useLoggerParams';

export const debug = debugFn('logger');

const DEFAULT_FOCUS_DURATION_MS = STREAM_SCREEN_CONFIG.defaultFocusLengthMins * 60 * 1000;

interface ApplyUrlParamsToStoreSetters {
  setUserRange: (from: number, to: number) => void;
  setCursor: (ts: number) => void;
  setFocus: (focus: { from: number; to: number }) => void;
  setFocusCenteredAtTs: (targetTs: number, duration: number) => void;
  setSelection: (stationId: string, tsA: number, tsB: number) => void;
  stationId: string;
}

/**
 * Applies URL params (range, cursor, selection) to the stream store and sets focus accordingly.
 * Range is always from/to. Focus is centered at cursor (if present), else at selection start (if present), else derived from range.
 */
function applyUrlParamsToStore(
  params: LoggerParamsWithRange,
  setters: ApplyUrlParamsToStoreSetters,
): void {
  const { from, to, cursor, selection } = params;
  const { setUserRange, setCursor, setFocus, setFocusCenteredAtTs, setSelection, stationId } =
    setters;

  debug(
    'Sync URL to store: range %s to %s',
    new Date(from).toLocaleString(),
    new Date(to).toLocaleString(),
  );

  setUserRange(from, to);

  if (cursor != null) {
    debug('Sync cursor to %s', new Date(cursor).toLocaleString());
    setCursor(cursor);
    setFocusCenteredAtTs(cursor, DEFAULT_FOCUS_DURATION_MS);
  } else if (selection != null) {
    debug('Sync focus to selection start %s', new Date(selection.from).toLocaleString());
    setFocusCenteredAtTs(selection.from, DEFAULT_FOCUS_DURATION_MS);
    setSelection(stationId, selection.from, selection.to);
  } else {
    const [focusFrom, focusTo] = calculateFocusFromRange([from, to]);
    setFocus({
      from: Number(focusFrom),
      to: Number(focusTo),
    });
  }
}

/**
 * Initializes the stream screen from URL (source of truth).
 * - When from/to are missing: computes default range (from cursor or "last hour") and writes to URL.
 * - Syncs URL params to store (range, cursor, focus). Focus is internal and never read from URL.
 * - Does not sync store back to URL; datepicker/useLoadRange writes from/to when user sets range.
 */
export function useInitializeStream() {
  const station = useActiveStation();
  const [urlParams, setUrlParams] = useLoggerParams();
  const setUserRange = useSetUserRange();
  const setCursor = useSetCursor();
  const setFocus = useSetFocus();
  const setFocusCenteredAtTs = useSetFocusCenteredAtTs();
  const setSelection = useSetSelectionAction();
  const userRange = useStreamUserDefinedRange();

  // Ensure URL has range (from/to), then sync URL -> store (range, cursor, selection, focus).
  // Selection is independent of range; when present, focus is centered at selection start (like cursor).
  useEffect(() => {
    if (urlParams.from == null || urlParams.to == null) {
      debug('Missing range in URL, computing defaults from cursor or selection');
      const [from, to] = calculateRangeFromConfig({
        cursor: urlParams.cursor ?? undefined,
        selection: urlParams.selection ?? undefined,
      });
      setUrlParams({ from, to }, { history: 'replace' });
      return;
    }

    applyUrlParamsToStore(
      {
        from: urlParams.from,
        to: urlParams.to,
        cursor: urlParams.cursor ?? null,
        selection: urlParams.selection ?? null,
      },
      {
        stationId: station.id,
        setUserRange,
        setCursor,
        setFocus,
        setFocusCenteredAtTs,
        setSelection,
      },
    );
  }, [
    urlParams.from,
    urlParams.to,
    urlParams.selection,
    urlParams.cursor,
    station.id,
    setUrlParams,
    setUserRange,
    setCursor,
    setFocus,
    setFocusCenteredAtTs,
  ]);

  return { initialized: !userRange.isLoading };
}
